/**
 * Design System Typography Component
 *
 * Wraps MUI Typography with design system token integration.
 * Provides consistent typography styling across the application.
 *
 * Features:
 * - All typography variants (h1-h6, body1-2, subtitle1-2, caption, overline)
 * - Semantic color names (primary, secondary, success, error, warning, info, neutral)
 * - Custom sx prop support for additional styling
 * - Proper MUI component mapping for accessibility
 * - Responsive font sizing support
 *
 * Usage:
 * <DSTypography variant="h2" color="primary">
 *   Page Title
 * </DSTypography>
 *
 * <DSTypography variant="body1" color="neutral">
 *   Body text with neutral color
 * </DSTypography>
 *
 * <DSTypography variant="caption" color="secondary">
 *   Small secondary text
 * </DSTypography>
 */

import React from 'react';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * DSTypography component - Design System Typography wrapper
 *
 * @component
 * @param {string} variant - Typography variant (h1, h2, h3, h4, h5, h6, body1, body2, subtitle1, subtitle2, caption, overline)
 * @param {string} color - Color variant (primary, secondary, success, error, warning, info, neutral, inherit, textPrimary, textSecondary)
 * @param {object} sx - Additional MUI sx prop for custom styling
 * @param {boolean} gutterBottom - Add bottom margin
 * @param {boolean} noWrap - Truncate text with ellipsis
 * @param {React.ReactNode} children - Text content
 * @param {...rest} rest - Any other MUI Typography props
 *
 * @returns {React.ReactElement} Typography component with design system styling
 */
const DSTypography = React.memo(
  React.forwardRef(
    (
      {
        variant = 'body1',
        color = 'inherit',
        sx = {},
        gutterBottom = false,
        noWrap = false,
        children,
        ...rest
      },
      ref
    ) => {
      // Map semantic color names to theme palette colors
      const colorMap = {
        primary: 'primary',
        secondary: 'secondary',
        success: 'success',
        error: 'error',
        warning: 'warning',
        info: 'info',
        neutral: 'text.primary',
        inherit: 'inherit',
        textPrimary: 'text.primary',
        textSecondary: 'text.secondary',
      };

      // Determine the actual color to use
      const finalColor = colorMap[color] || color;

      // Build comprehensive sx prop
      const finalSx = {
        // Apply color from theme
        ...(finalColor !== 'inherit' && {
          color: `${finalColor}`,
        }),

        // Add design system typography styling
        ...(variant.startsWith('h') && {
          fontFamily: '"Montserrat", sans-serif',
        }),

        // Responsive adjustments for mobile
        [`@media (max-width: 576px)`]: {
          ...(variant === 'h1' && { fontSize: '36px' }),
          ...(variant === 'h2' && { fontSize: '32px' }),
          ...(variant === 'h3' && { fontSize: '28px' }),
          ...(variant === 'h4' && { fontSize: '20px' }),
        },

        // Merge custom sx props (custom overrides take precedence)
        ...sx,
      };

      return (
        <Typography
          ref={ref}
          variant={variant}
          gutterBottom={gutterBottom}
          noWrap={noWrap}
          sx={finalSx}
          {...rest}
        >
          {children}
        </Typography>
      );
    }
  )
);

// Display name for debugging
DSTypography.displayName = 'DSTypography';

// PropTypes for type checking
DSTypography.propTypes = {
  /**
   * The typography variant to use
   */
  variant: PropTypes.oneOf([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'caption',
    'overline',
    'button',
  ]),

  /**
   * The color variant to apply
   */
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'error',
    'warning',
    'info',
    'neutral',
    'inherit',
    'textPrimary',
    'textSecondary',
  ]),

  /**
   * Additional MUI sx prop for custom styling
   */
  sx: PropTypes.object,

  /**
   * If true, adds bottom margin
   */
  gutterBottom: PropTypes.bool,

  /**
   * If true, prevents text wrapping with ellipsis
   */
  noWrap: PropTypes.bool,

  /**
   * Text content to display
   */
  children: PropTypes.node.isRequired,

  /**
   * HTML element or custom component to render as
   */
  component: PropTypes.elementType,

  /**
   * Align text content
   */
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
};


export default DSTypography;
